import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  Platform,
  RefreshControl,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Text } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useBottomTabBarHeight } from '@react-navigation/bottom-tabs';
import { useTranslation } from 'react-i18next';

import JobsContext from '../context/JobsContext';
import { JobType } from '../types';
import { Palette, radius, spacing, useThemePalette } from '../theme';
import { track } from '../lib/analytics';
import { computeFitScore, getPrimaryResume } from '../lib/match/fitScore';
import { jobMatchesAlert } from '../lib/alerts/match';
import Job from './Job';
import Error from './Error';
import FilterModal from './FilterModal';
import SettingsModal from './SettingsModal';
import NativeAdCard from './NativeAdCard';

const PageStructuredData: React.FC = () => {
  if (Platform.OS !== 'web') return null;

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'WebSite',
    name: 'RemoteWork',
    description: 'Find remote job opportunities worldwide',
    url: 'https://remotework.example.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://remotework.example.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return React.createElement('script', {
    type: 'application/ld+json',
    dangerouslySetInnerHTML: { __html: JSON.stringify(schema) },
  });
};

interface ListJobsProps {
  refresh: () => void;
  jobs: JobType[];
  refreshing: boolean;
  error?: boolean;
  clearFavorites?: () => void;
  title?: string;
  // Pre-fill the search (e.g. when arriving from a saved alert's "matching
  // now" tap). Opens the search bar and filters the list to these keywords.
  initialSearch?: string;
  // Shown in place of the list when there are no items to display (e.g. the
  // favorites screen with nothing saved yet).
  emptyMessage?: string;
}

type ListItem = { kind: 'job'; job: JobType } | { kind: 'ad'; key: string };

const threeMonthsAgo = (): Date => {
  const d = new Date();
  // Pin to the 1st first so day-overflow (e.g. May 31 → Feb 31) can't
  // silently roll into the next month before we subtract.
  d.setDate(1);
  d.setMonth(d.getMonth() - 3);
  return d;
};
const initialDate: Date = threeMonthsAgo();

const ListJobs: React.FC<ListJobsProps> = ({
  jobs,
  refreshing,
  refresh,
  error,
  clearFavorites,
  title,
  initialSearch,
  emptyMessage,
}) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { applied, resumes } = useContext(JobsContext);
  // RN-Web positions the bottom tab bar absolutely, so the FlatList slides
  // under it. Native already insets content. Pad with the actual height so
  // the last row clears the tab bar on every platform.
  const tabBarHeight = useBottomTabBarHeight();
  const styles = useMemo(() => makeStyles(palette, tabBarHeight), [palette, tabBarHeight]);
  const [filterText, setFilterText] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [pickedDate, setPickedDate] = useState(initialDate.toJSON());
  const [pickedTags, setPickedTags] = useState<string[]>([]);
  const [showAppliedOnly, setShowAppliedOnly] = useState(false);
  const [sortByFit, setSortByFit] = useState(false);

  // The resume we score against (most-recently-updated). Null = no resume, so
  // the sort toggle stays hidden. The per-job fit chip is computed inside each
  // Job row (only the visible rows) — we do NOT score the whole list here.
  const primaryResume = useMemo(() => getPrimaryResume(resumes), [resumes]);
  // Scores for the whole list are only needed to sort, and only when the user
  // turns sorting on. Memoized on stable inputs so it runs at most once per
  // resume/list change instead of on every render.
  const fitScores = useMemo(() => {
    if (!sortByFit || !primaryResume) return null;
    const map = new Map<string, number>();
    jobs.forEach((job) => map.set(job.id, computeFitScore(primaryResume, job).score));
    return map;
  }, [sortByFit, primaryResume, jobs]);

  // Arriving from a saved alert (or any deep link) pre-fills + opens the search.
  useEffect(() => {
    if (initialSearch && initialSearch.trim()) {
      setSearchOpen(true);
      setFilterText(initialSearch);
    }
  }, [initialSearch]);

  // Match every keyword (whole-word) across position + tags + description —
  // the same logic as job alerts, so "matching now" and the filtered list
  // agree. Reusing the matcher also avoids the old unescaped-regex crash on
  // input like "(".
  const textFilter = (job: JobType) =>
    !filterText.trim() ||
    jobMatchesAlert(job, {
      id: '',
      query: filterText,
      tags: [],
      enabled: true,
      createdAt: '',
      updatedAt: '',
    });
  const dateFilter = (job: JobType) => Date.parse(job.date) >= Date.parse(pickedDate);
  const tagFilter = (job: JobType) =>
    job.tags?.some((tag) => pickedTags.includes(tag)) || false;

  const appliedFilter = (job: JobType) => !showAppliedOnly || applied.includes(job.id);
  const tagFilteredData = pickedTags.length > 0 ? jobs.filter(tagFilter) : jobs;
  const textFilteredData = tagFilteredData.filter(textFilter);
  const appliedFilteredData = textFilteredData.filter(appliedFilter);
  const filteredData = appliedFilteredData.filter(dateFilter);

  const clearAllFilter = () => {
    setPickedDate(initialDate.toJSON());
    setPickedTags([]);
    setFilterText('');
    setShowAppliedOnly(false);
  };

  const activeFilterCount =
    (pickedDate !== initialDate.toJSON() ? 1 : 0) +
    (pickedTags.length > 0 ? 1 : 0) +
    (showAppliedOnly ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  // Intersperse a native ad after every Nth job. First ad lands after a
  // full screen of organic content so the user sees the feed first; spacing
  // keeps content-to-ad ratio comfortably inside AdMob policy.
  const AD_INTERVAL = 12;
  const itemsWithAds = useMemo<ListItem[]>(() => {
    const ordered =
      sortByFit && fitScores
        ? [...filteredData].sort(
            (a, b) => (fitScores.get(b.id) ?? 0) - (fitScores.get(a.id) ?? 0)
          )
        : filteredData;
    const items: ListItem[] = [];
    ordered.forEach((job, idx) => {
      items.push({ kind: 'job', job });
      if ((idx + 1) % AD_INTERVAL === 0) {
        items.push({ kind: 'ad', key: `ad-${idx + 1}` });
      }
    });
    return items;
  }, [filteredData, sortByFit, fitScores]);

  const renderItem = ({ item }: { item: ListItem }) =>
    item.kind === 'ad' ? <NativeAdCard /> : <Job data={item.job} />;

  const keyExtractor = (item: ListItem) =>
    item.kind === 'ad' ? item.key : item.job.id;

  if (error) {
    return <Error onPress={refresh} />;
  }

  const headerTitle = title ?? t('tabs.allJobs');

  const toggleSearch = () => {
    if (searchOpen) {
      setSearchOpen(false);
      setFilterText('');
    } else {
      setSearchOpen(true);
      track('search_open');
    }
  };

  return (
    <SafeAreaView style={styles.safeArea} edges={['top']}>
      <View style={styles.container}>
        <PageStructuredData />

        <View style={styles.header}>
          <Text style={styles.headerTitle}>{headerTitle}</Text>
          {primaryResume && (
            <TouchableOpacity
              onPress={() => {
                const next = !sortByFit;
                setSortByFit(next);
                track('fit_sort_toggle', { value: next });
              }}
              style={[styles.headerBtn, sortByFit && styles.headerBtnActive]}
              hitSlop={8}
              accessibilityLabel={t('sort.toggle')}
            >
              <MaterialCommunityIcons
                name="sort-variant"
                size={22}
                color={sortByFit ? palette.primary : palette.textPrimary}
              />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={toggleSearch}
            style={[styles.headerBtn, searchOpen && styles.headerBtnActive]}
            hitSlop={8}
            accessibilityLabel={searchOpen ? t('search.close') : t('search.open')}
          >
            <MaterialCommunityIcons
              name={searchOpen ? 'close' : 'magnify'}
              size={22}
              color={palette.textPrimary}
            />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              setShowFilterModal(true);
              track('filter_open');
            }}
            style={styles.headerBtn}
            hitSlop={8}
            accessibilityLabel={t('search.filter')}
          >
            <View>
              <MaterialCommunityIcons
                name="filter-variant"
                size={22}
                color={palette.textPrimary}
              />
              {activeFilterCount > 0 && (
                <View style={styles.filterBadge}>
                  <Text style={styles.filterBadgeText}>{activeFilterCount}</Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              setShowSettingsModal(true);
              track('settings_open');
            }}
            style={styles.headerBtn}
            hitSlop={8}
            accessibilityLabel={t('settings.open')}
          >
            <MaterialCommunityIcons
              name="cog-outline"
              size={22}
              color={palette.textPrimary}
            />
          </TouchableOpacity>
        </View>

        {searchOpen && (
          <View style={styles.searchBarWrap}>
            <View style={styles.searchBar}>
              <MaterialCommunityIcons
                name="magnify"
                size={20}
                color={palette.textMuted}
              />
              <TextInput
                placeholder={t('search.placeholder')}
                placeholderTextColor={palette.textMuted}
                value={filterText}
                onChangeText={setFilterText}
                style={styles.searchInput}
                autoFocus
                returnKeyType="search"
                underlineColorAndroid="transparent"
              />
              {filterText.length > 0 && (
                <TouchableOpacity
                  onPress={() => setFilterText('')}
                  hitSlop={8}
                  accessibilityLabel={t('search.clear')}
                >
                  <MaterialCommunityIcons
                    name="close-circle"
                    size={18}
                    color={palette.textMuted}
                  />
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        <FlatList
          data={itemsWithAds}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} />}
          ListEmptyComponent={
            emptyMessage ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons
                  name="heart-outline"
                  size={44}
                  color={palette.textMuted}
                />
                <Text style={styles.emptyText}>{emptyMessage}</Text>
              </View>
            ) : null
          }
          style={styles.list}
          contentContainerStyle={
            itemsWithAds.length === 0 ? styles.emptyListContent : styles.listContent
          }
          keyboardShouldPersistTaps="handled"
          // Perf tuning: cuts cold-start time on the 5k+ job feed by deferring
          // off-screen rendering. removeClippedSubviews = native UIView pruning.
          initialNumToRender={10}
          maxToRenderPerBatch={10}
          windowSize={7}
          removeClippedSubviews={Platform.OS !== 'web'}
        />

        <FilterModal
          visible={showFilterModal}
          onDismiss={() => setShowFilterModal(false)}
          jobs={jobs}
          pickedDate={pickedDate}
          setPickedDate={setPickedDate}
          pickedTags={pickedTags}
          setPickedTags={setPickedTags}
          showAppliedOnly={showAppliedOnly}
          setShowAppliedOnly={setShowAppliedOnly}
          clearFilters={clearAllFilter}
          hasActiveFilters={hasActiveFilters}
          clearFavorites={clearFavorites}
          appliedIds={applied}
        />

        <SettingsModal
          visible={showSettingsModal}
          onDismiss={() => setShowSettingsModal(false)}
        />
      </View>
    </SafeAreaView>
  );
};

// Mobile-first single-column reading width on web. Below this breakpoint
// the cap is a no-op since `width: 100%` constrains it to the viewport.
const CONTENT_MAX_WIDTH = 720;
const isWeb = Platform.OS === 'web';

const makeStyles = (p: Palette, tabBarHeight: number) =>
  StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: p.canvas },
    container: {
      flex: 1,
      width: '100%',
      maxWidth: isWeb ? CONTENT_MAX_WIDTH : undefined,
      alignSelf: isWeb ? 'center' : undefined,
      backgroundColor: p.canvas,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.md,
      paddingBottom: spacing.sm,
      gap: spacing.xs,
    },
    headerTitle: { flex: 1, color: p.textPrimary, fontSize: 18, fontWeight: '700' },
    headerBtn: {
      width: 36,
      height: 36,
      borderRadius: radius.pill,
      alignItems: 'center',
      justifyContent: 'center',
    },
    headerBtnActive: {
      backgroundColor: p.surfaceMuted,
    },
    filterBadge: {
      position: 'absolute',
      top: -6,
      right: -8,
      minWidth: 16,
      height: 16,
      borderRadius: 8,
      paddingHorizontal: 4,
      backgroundColor: p.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    filterBadgeText: {
      color: '#0F1115',
      fontSize: 10,
      fontWeight: '700',
      lineHeight: 12,
    },
    list: { flex: 1 },
    listContent: { paddingBottom: spacing.lg + (isWeb ? tabBarHeight : 0) },
    emptyListContent: { flexGrow: 1, paddingBottom: spacing.lg + (isWeb ? tabBarHeight : 0) },
    emptyState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: spacing.xl,
      paddingTop: spacing.xxl,
      gap: spacing.md,
    },
    emptyText: {
      fontSize: 15,
      color: p.textSecondary,
      textAlign: 'center',
      lineHeight: 22,
    },
    searchBarWrap: {
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.sm,
    },
    searchBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: p.surface,
      borderRadius: radius.pill,
      borderWidth: 1,
      borderColor: p.border,
      paddingHorizontal: spacing.md,
      height: 44,
    },
    searchInput: {
      flex: 1,
      fontSize: 14,
      color: p.textPrimary,
      paddingVertical: 0,
    },
  });

export default ListJobs;
